/* Subscribe page — 3 continuity tiers for existing kit buyers.
   Flow: enter email -> tier card -> Stripe Checkout -> /subscribe/success */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Sparkles, ArrowRight, AlertCircle } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Subscribe() {
    const { lang } = useT();
    const [tiers, setTiers] = useState([]);
    const [mode, setMode] = useState(null);
    const [params] = useSearchParams();
    const orderId = params.get("from") || "";
    const [email, setEmail] = useState(params.get("email") || "");
    const [status, setStatus] = useState(null);
    const [loadingTier, setLoadingTier] = useState(null);

    useEffect(() => {
        axios.get(`${API}/subscriptions/tiers`).then((r) => setTiers(r.data || [])).catch(() => {});
        axios.get(`${API}/subscriptions/mode`).then((r) => setMode(r.data?.mode)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!email || !email.includes("@")) { setStatus(null); return; }
        const t = setTimeout(() => {
            axios.post(`${API}/subscriptions/status`, { email })
                .then((r) => setStatus(r.data))
                .catch(() => setStatus(null));
        }, 400);
        return () => clearTimeout(t);
    }, [email]);

    const startCheckout = async (tier) => {
        if (!email || !email.includes("@")) {
            // Don't silently fail — guide the user to the email input. Without
            // this, the button looks broken because it's disabled with no
            // visible reason. Toast + autofocus + scroll-to is the friendly fix.
            toast.error(lang === "cn" ? "请先输入 email" : "Please enter your email first");
            const input = document.querySelector("[data-testid='email-input']");
            if (input) {
                input.focus();
                input.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
        }
        setLoadingTier(tier.id);
        try {
            const { data } = await axios.post(`${API}/subscriptions/checkout`, {
                tier: tier.id,
                email,
                origin_url: window.location.origin,
                order_id: orderId || undefined,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Stripe checkout failed");
            setLoadingTier(null);
        }
    };

    const openPortal = async () => {
        try {
            const { data } = await axios.post(`${API}/subscriptions/portal`, {
                email, origin_url: window.location.origin,
            });
            window.location.href = data.portal_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Portal unavailable");
        }
    };

    return (
        <div className="min-h-screen bg-cafe-cream">
            <SiteNav />
            <main className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-20" data-testid="subscribe-page">
                <div className="text-center mb-10">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold mb-3">
                        {lang === "cn" ? "持续服务 · 月度订阅" : "Continuity · Monthly Subscription"}
                    </p>
                    <h1 className="font-serif text-4xl sm:text-5xl text-cafe-espresso text-balance">
                        {lang === "cn"
                            ? "你的 IG 不止活到第 30 天。"
                            : "Your IG shouldn't stop on Day 30."}
                    </h1>
                    <p className="text-base text-cafe-mocha mt-5 max-w-2xl mx-auto leading-relaxed">
                        {lang === "cn"
                            ? "Kit 交付之后，IG 算法每个月都在变。订阅持续服务，让你的内容跟上节奏。"
                            : "After your kit ships, the IG algorithm shifts every month. Subscribe to stay in step."}
                    </p>
                </div>

                {/* Email gate */}
                <div className="bg-white border border-border rounded-2xl p-5 mb-8 max-w-md mx-auto" data-testid="email-gate">
                    <label className="text-[10px] uppercase tracking-wider text-cafe-mocha font-bold block mb-2">
                        {lang === "cn" ? "请使用购买 Kit 时的 email" : "Use the email from your kit order"}
                    </label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-cafe-cream"
                        data-testid="email-input"
                    />
                    {status?.has_subscription && (
                        <div className="mt-3 bg-cafe-olive/10 border border-cafe-olive/30 rounded-lg p-3 text-xs flex items-start gap-2" data-testid="active-sub-banner">
                            <Check className="h-4 w-4 text-cafe-olive shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-cafe-espresso font-bold">
                                    {lang === "cn" ? "你已是订户" : "You're already subscribed"} — <span className="font-mono">{status.tier}</span>
                                </p>
                                <p className="text-cafe-mocha mt-0.5">
                                    {lang === "cn" ? "状态" : "Status"}: {status.status}
                                    {status.cancel_at_period_end && ` · ${lang === "cn" ? "已设定在周期结束时取消" : "ending at period close"}`}
                                </p>
                                <button onClick={openPortal} className="mt-1.5 text-cafe-olive text-xs font-bold uppercase tracking-wider underline" data-testid="open-portal">
                                    {lang === "cn" ? "打开 Stripe 后台 · 修改/取消 →" : "Open Stripe Portal · Manage / Cancel →"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Tier grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="tier-grid">
                    {tiers.map((t) => (
                        <TierCard
                            key={t.id}
                            tier={t}
                            lang={lang}
                            isLoading={loadingTier === t.id}
                            disabled={status?.has_subscription}
                            onClick={() => startCheckout(t)}
                        />
                    ))}
                </div>

                <p className="text-center text-xs text-cafe-mocha italic mt-8 max-w-xl mx-auto leading-relaxed">
                    {mode === "test" ? (
                        lang === "cn"
                            ? "测试模式 · 使用卡号 4242 4242 4242 4242 可完成流程而不实际扣款。"
                            : "Test mode · Use card number 4242 4242 4242 4242 to complete the flow without a real charge."
                    ) : (
                        lang === "cn"
                            ? "由 Stripe 安全处理支付 · 支持 Visa / Mastercard / Amex · 随时透过 Stripe portal 取消"
                            : "Secure payment via Stripe · Visa / Mastercard / Amex · Cancel anytime via Stripe portal"
                    )}
                </p>
            </main>
            <SiteFooter />
        </div>
    );
}

function TierCard({ tier, lang, onClick, isLoading, disabled }) {
    const name = lang === "cn" ? tier.name_cn : tier.name_en;
    const tagline = lang === "cn" ? tier.tagline_cn : tier.tagline_en;
    const features = lang === "cn" ? tier.features_cn : tier.features_en;
    return (
        <div
            className={`relative bg-white border-2 rounded-2xl p-6 flex flex-col ${tier.popular ? "border-cafe-olive" : "border-border"}`}
            data-testid={`tier-card-${tier.id}`}
        >
            {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cafe-olive text-cafe-cream text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-full">
                    {lang === "cn" ? "最受欢迎" : "Most popular"}
                </div>
            )}
            <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold mb-2">{name}</p>
            <p className="font-serif text-4xl text-cafe-espresso">
                RM {tier.price_rm}<span className="text-base text-cafe-mocha font-normal">/{lang === "cn" ? "月" : "mo"}</span>
            </p>
            <p className="text-sm text-cafe-mocha leading-relaxed mt-3 mb-5">{tagline}</p>
            <ul className="space-y-2 text-sm text-cafe-espresso flex-1">
                {features.map((f, i) => (
                    <li key={i} className="flex gap-2">
                        <Check className="h-4 w-4 text-cafe-olive shrink-0 mt-0.5" />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>
            <button
                onClick={onClick}
                disabled={disabled || isLoading}
                className={`mt-5 rounded-full px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${tier.popular ? "bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream" : "bg-cafe-espresso hover:bg-cafe-mocha text-cafe-cream"}`}
                data-testid={`subscribe-${tier.id}`}
            >
                {isLoading ? (lang === "cn" ? "跳转中…" : "Redirecting…") : (
                    <>
                        <Sparkles className="h-3.5 w-3.5" />
                        {lang === "cn" ? `订阅 RM ${tier.price_rm}/月` : `Subscribe · RM ${tier.price_rm}/mo`}
                        <ArrowRight className="h-3.5 w-3.5" />
                    </>
                )}
            </button>
        </div>
    );
}

/** /subscribe/success — Stripe redirects here after checkout.
 *  We poll /checkout-status until payment is confirmed. */
export function SubscribeSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [tx, setTx] = useState(null);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { lang } = useT();

    useEffect(() => {
        if (!sessionId) { setError("Missing session_id"); return; }
        let attempts = 0;
        const tick = async () => {
            attempts++;
            try {
                const { data } = await axios.get(`${API}/subscriptions/checkout-status/${sessionId}`);
                setTx(data);
                if (data.payment_status === "paid") {
                    // Redirect to /welcome so they set a password before accessing Member Hub
                    navigate(`/welcome?session_id=${encodeURIComponent(sessionId)}&product=subscription`, { replace: true });
                    return;
                }
                if (attempts >= 8) return;
                setTimeout(tick, 2500);
            } catch (e) {
                setError(e?.response?.data?.detail || "Status check failed");
            }
        };
        tick();
    }, [sessionId, navigate]);

    const paid = tx?.payment_status === "paid";
    return (
        <div className="min-h-screen bg-cafe-cream">
            <SiteNav />
            <main className="max-w-2xl mx-auto px-5 sm:px-8 pt-24 pb-20 text-center" data-testid="subscribe-success">
                {error ? (
                    <>
                        <AlertCircle className="h-12 w-12 text-cafe-terracotta mx-auto mb-4" />
                        <h1 className="font-serif text-3xl text-cafe-espresso">{lang === "cn" ? "出错了" : "Something went wrong"}</h1>
                        <p className="text-cafe-mocha mt-3">{error}</p>
                    </>
                ) : paid ? (
                    <>
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-cafe-olive/15 mb-5">
                            <Check className="h-7 w-7 text-cafe-olive" />
                        </div>
                        <h1 className="font-serif text-4xl text-cafe-espresso">
                            {lang === "cn" ? "订阅生效。" : "You're in."}
                        </h1>
                        <p className="text-cafe-mocha mt-4 leading-relaxed">
                            {lang === "cn"
                                ? `欢迎成为 ${tx.tier} 订户。我们已发送会员中心登录链接到你的邮箱 — 点击即可查看本月 digest 与管理订阅。`
                                : `Welcome aboard ${tx.tier}. Check your inbox — we've emailed a magic link to your Member Hub where you can read this month's digest and manage your subscription.`}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
                            <button
                                onClick={async () => {
                                    if (tx?.email) {
                                        try { await axios.post(`${API}/member/login`, { email: tx.email, origin_url: window.location.origin }); } catch {}
                                    }
                                    navigate(lang === "cn" ? "/member/cn" : "/member");
                                }}
                                className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream px-7 py-3 text-xs font-bold uppercase tracking-[0.14em]"
                                data-testid="subscribe-success-member-hub"
                            >
                                {lang === "cn" ? "打开会员中心" : "Open Member Hub"}
                            </button>
                            <button
                                onClick={() => navigate("/")}
                                className="rounded-full border border-cafe-espresso/30 hover:border-cafe-espresso text-cafe-espresso px-7 py-3 text-xs font-bold uppercase tracking-[0.14em]"
                                data-testid="back-home"
                            >
                                {lang === "cn" ? "返回首页" : "Back to home"}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="inline-block w-10 h-10 border-2 border-cafe-olive/30 border-t-cafe-olive rounded-full animate-spin mb-4"></div>
                        <h1 className="font-serif text-3xl text-cafe-espresso">
                            {lang === "cn" ? "正在确认你的支付…" : "Confirming your payment…"}
                        </h1>
                        <p className="text-cafe-mocha mt-3 text-sm">
                            {lang === "cn"
                                ? "这通常只需几秒。请勿关闭页面。"
                                : "This usually takes a few seconds. Please don't close the page."}
                        </p>
                    </>
                )}
            </main>
            <SiteFooter />
        </div>
    );
}
